import React, { memo } from 'react'
import dayjs from 'dayjs'
import styled from 'styled-components'
import { useStateContext } from '../../../../context/StateProvider'
import { useOneClickPrescribeContext } from '../../context/OneClickPrescribeContext'
import CaseCard from '../CaseCard'

import calcBMI from '../../../../utils/calcBMI';

import {
    CaseWrapper,
} from './styles'

const AnimatedCaseCard = styled.div`
    opacity: ${props => props.$isfadingout === 'true' ? 0 : 1};
    transition: opacity 0.3s ease-out;
    border-radius: 1rem;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08);
    background: transparent;
`;

const CaseComponent = ({
    flagged,
    caseData,
    isFadingOut,
    isCollapsing,
    isSuccessCollapsed,
    autoDismissProgress = 0,
}) => {
    if (!caseData) return null

    const {
        handleViewCase,
        handlePrescribe,
        handleDismissCase,
        dismissSuccessCase,
        onFadeComplete,
        onCollapseComplete,
        providerHasPinSet,
        pin,
    } = useOneClickPrescribeContext()

    const {
        category,
        patient,
        oneClickPrescribeAssessment = {},
        success,
        submitting,
        updatedDate,
        state,
        allergies,
        medications,
        bloodPressure,
        heightFt,
        heightIn,
        weight,
        requestedMedicines,
        requestType,
    } = caseData

    if (!patient || !category || !oneClickPrescribeAssessment?.outputs) {
        return null
    }
    
    const { outputs = {}, passedEvaluation = false } = oneClickPrescribeAssessment

    const assessmentDictionary = {
        questions: 'out-0',
        recommendation: 'out-1',
        summary: 'out-2',
    }

    const questions = JSON.parse(outputs[assessmentDictionary.questions]) || {}
    const summary = JSON.parse(outputs[assessmentDictionary.summary])

    const OCPClassification = questions.classification
    const OCPFlaggedQuestions = questions.flagged_questions || []

    const eligibilitySummary = summary.summary
    const hasTemplatesApplied = requestedMedicines?.every(med => med.rxTemplate) && caseData.messageTemplate
    const OCPEligibilityGuard = !hasTemplatesApplied
    const isAIFlagged = !passedEvaluation && hasTemplatesApplied

    const { getStateNameById } = useStateContext()

    const getInitials = (name) => {
        if (!name) return '??'
        const parts = name.trim().split(' ')
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }

    const patientName = `${patient.firstName} ${patient.lastName}`

    const patientData = {
        initials: getInitials(patientName),
        name: patientName,
        age: dayjs().utc().diff(dayjs(patient.dob), 'years'),
        dob: dayjs(patient.dob).utc().format("MM/DD/YYYY"),
        idVerified: true,
        location: getStateNameById(state),
        lastUpdated:  dayjs(updatedDate).format("MMM D, YYYY"),
    }

    const eligibility = {
        status: passedEvaluation ? 'Eligible' : 'Ineligible',
        flags: OCPFlaggedQuestions.length || 'No flags',
        description: eligibilitySummary,
        flaggedQuestions: OCPFlaggedQuestions.length > 0 ? OCPFlaggedQuestions : 'None',
        flagDetails: OCPClassification,
    }

    // Format height and weight
    const formatHeightWeight = () => {
        if (heightFt && heightIn) {
            return `${heightFt} ft ${heightIn} in · ${weight} lbs`
        }
        return 'N/A'
    }

    const medicalInfo = {
        allergies: allergies || 'None reported',
        medications: medications || 'None reported',
        bloodPressure: bloodPressure || 'N/A',
        visitType: `Async ${category.name}`,
        riskTier: 'n/a',
        heightWeight: formatHeightWeight(),
        bmi: calcBMI(heightFt, heightIn, weight) || 'N/A',
        requestedMedication: requestedMedicines[0],
        queuedPrescription: requestedMedicines[0].rxTemplate,
    }

    const pinRequirementSatisfied = !providerHasPinSet || (providerHasPinSet && pin?.length === 4)
    const isDisabled = submitting || !pinRequirementSatisfied || OCPEligibilityGuard
    const isOverrideDisabled = submitting || !pinRequirementSatisfied

    const handleDismiss = () => {
        const caseId = caseData.caseId || caseData._id
        // Trigger animation to remove the case (keep success state during animation)
        dismissSuccessCase(caseId, false) // isDirect = false for success dismissals
        // Update Redux state
        handleDismissCase(caseData, false) // isError = false
    }

    const handleErrorDismiss = () => {
        const caseId = caseData.caseId || caseData._id
        // Trigger animation to remove the case directly (no success state)
        dismissSuccessCase(caseId, true) // isDirect = true for error dismissals
        // Update Redux state and tags
        handleDismissCase(caseData, true) // isError = true
    }

    const caseId = caseData.caseId || caseData._id

    const handleFadeTransitionEnd = (e) => {
        // Only respond to opacity transitions on the immediate element
        if (e.propertyName === 'opacity' && e.target === e.currentTarget) {
            onFadeComplete(caseId)
        }
    }

    const handleCollapseTransitionEnd = (e) => {
        // Only respond to max-height transitions on the immediate element
        if (e.propertyName === 'max-height' && e.target === e.currentTarget) {
            onCollapseComplete(caseId)
        }
    }

    const handleOverridePrescribe = (feedback) => {
        handlePrescribe(caseData, feedback)
    }

    return (
        <CaseWrapper 
            $iscollapsing={isCollapsing.toString()}
            onTransitionEnd={handleCollapseTransitionEnd}
        >
            <AnimatedCaseCard 
                $isfadingout={isFadingOut.toString()}
                onTransitionEnd={handleFadeTransitionEnd}
            >
                <CaseCard
                    patient={patientData}
                    eligibility={eligibility}
                    medicalInfo={medicalInfo}
                    requestType={requestType}
                    onViewCase={() => handleViewCase(caseData._id)}
                    onPrescribe={() => handlePrescribe(caseData)}
                    onOverridePrescribe={handleOverridePrescribe}
                    onDismiss={handleDismiss}
                    onErrorDismiss={handleErrorDismiss}
                    isSubmitting={caseData.submitting}
                    hasError={OCPEligibilityGuard || isAIFlagged}
                    isFlagged={flagged}
                    isAIFlagged={isAIFlagged}
                    eligibilityGuardTriggered={OCPEligibilityGuard}
                    isDisabled={isDisabled}
                    isOverrideDisabled={isOverrideDisabled}
                    isSuccessCollapsed={isSuccessCollapsed}
                    autoDismissProgress={autoDismissProgress}
                />
            </AnimatedCaseCard>
        </CaseWrapper>
    )
}

const MemoizedCaseComponent = memo(CaseComponent, (prevProps, nextProps) => {
    const prevCaseId = prevProps.caseData?.caseId || prevProps.caseData?._id;
    const nextCaseId = nextProps.caseData?.caseId || nextProps.caseData?._id;

    return (
        prevCaseId === nextCaseId &&
        prevProps.isFadingOut === nextProps.isFadingOut &&
        prevProps.isCollapsing === nextProps.isCollapsing &&
        prevProps.isSuccessCollapsed === nextProps.isSuccessCollapsed &&
        prevProps.caseData?.submitting === nextProps.caseData?.submitting &&
        prevProps.caseData?.success === nextProps.caseData?.success &&
        prevProps.autoDismissProgress === nextProps.autoDismissProgress
    );
});

MemoizedCaseComponent.displayName = 'CaseComponent';

export default MemoizedCaseComponent